import React, { useState, useRef, useEffect } from "react";
import { FaChevronDown, FaClock } from "react-icons/fa";

/**
 * SlotDropdown
 * Same visual style as SortDropdown, adapted for delivery time-slot selection.
 *
 * Props:
 *  value          – currently selected slot value/label string
 *  onChange       – fn(slotValue) called on selection
 *  slots          – array from API: [{ slot_id, label, value, day }, ...]
 *  placeholder    – text shown when nothing is selected
 */
const SlotDropdown = ({
  value = "",
  onChange,
  slots = [],
  placeholder = "Select Delivery Slot",
}) => {
  const [open, setOpen] = useState(false);
  const containerRef = useRef(null);

  /* Close on outside click */
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target))
        setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  /* Close on Escape */
  useEffect(() => {
    const handler = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);


  /* Fallback default slots when API returns nothing */
  const fallbackSlots = [
    { slot_id: "f1", label: "Today: 04:00 PM – 06:00 PM", value: "Today: 04:00 PM - 06:00 PM", day: "Today" },
    { slot_id: "f2", label: "Today: 06:00 PM – 08:00 PM", value: "Today: 06:00 PM - 08:00 PM", day: "Today" },
    { slot_id: "f3", label: "Tomorrow: 08:00 AM – 10:00 AM", value: "Tomorrow: 08:00 AM - 10:00 AM", day: "Tomorrow" },
    { slot_id: "f4", label: "Tomorrow: 10:00 AM – 12:00 PM", value: "Tomorrow: 10:00 AM - 12:00 PM", day: "Tomorrow" },
    { slot_id: "f5", label: "Tomorrow: 04:00 PM – 06:00 PM", value: "Tomorrow: 04:00 PM - 06:00 PM", day: "Tomorrow" },
    { slot_id: "f6", label: "Tomorrow: 06:00 PM – 08:00 PM", value: "Tomorrow: 06:00 PM - 08:00 PM", day: "Tomorrow" },
  ];

  const effectiveSlots = slots.length > 0 ? slots : fallbackSlots;
  const effectiveGroups = effectiveSlots.reduce((acc, slot) => {
    const day = slot.day
      ? String(slot.day).charAt(0).toUpperCase() + String(slot.day).slice(1).toLowerCase()
      : "Other";
    if (!acc[day]) acc[day] = [];
    acc[day].push(slot);
    return acc;
  }, {});

  /* Day color palette */
  const dayConfig = {
    Today:    { color: "#16a34a", bg: "#dcfce7" },
    Tomorrow: { color: "#0ea5e9", bg: "#e0f2fe" },
    Other:    { color: "#6366f1", bg: "#eef2ff" },
  };
  const getDayConfig = (day) => dayConfig[day] || dayConfig["Other"];

  /* Find selected slot label */
  const selectedSlot = effectiveSlots.find(
    (s) => String(s.value || s.label) === String(value) || String(s.slot_id) === String(value)
  );
  const selectedLabel = selectedSlot?.label || selectedSlot?.value || value || "";

  /* Determine which day the selected slot belongs to */
  const selectedDay = selectedSlot?.day
    ? String(selectedSlot.day).charAt(0).toUpperCase() + String(selectedSlot.day).slice(1).toLowerCase()
    : null;
  const selectedConfig = selectedDay ? getDayConfig(selectedDay) : { color: "#046a38", bg: "#dcfce7" };

  const handleSelect = (slot) => {
    const slotVal = slot.value || slot.label;
    if (onChange) onChange(slotVal, slot.slot_id != null ? Number(slot.slot_id) : null);
    setOpen(false);
  };

  return (
    <div
      ref={containerRef}
      className="relative w-full"
      style={{ zIndex: 40 }}
      onClick={(e) => e.stopPropagation()}
    >
      {/* Trigger button */}
      <button
        type="button"
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((prev) => !prev)}
        className="flex w-full items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition-all duration-200 hover:border-emerald-400 hover:shadow-md focus:outline-none"
        style={
          open
            ? { borderColor: selectedConfig.color, boxShadow: `0 0 0 2px ${selectedConfig.color}22` }
            : {}
        }
      >
        {/* Icon bubble */}
        <span
          className="flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-md transition-all duration-200"
          style={{ background: selectedConfig.bg, color: selectedConfig.color }}
        >
          <FaClock className="h-2.5 w-2.5" aria-hidden="true" />
        </span>

        {/* Label */}
        <span className="flex-1 min-w-0 text-left leading-tight">
          <span className="block text-[9px] font-medium text-gray-400">Delivery Slot</span>
          <span className="block truncate text-[10px] font-bold text-gray-800">
            {selectedLabel || placeholder}
          </span>
        </span>

        {/* Chevron */}
        <FaChevronDown
          className="h-2.5 w-2.5 flex-shrink-0 text-gray-400 transition-transform duration-300"
          style={{ transform: open ? "rotate(180deg)" : "rotate(0deg)" }}
          aria-hidden="true"
        />
      </button>

      {/* Dropdown panel */}
      {open && (
        <div
          role="listbox"
          aria-label="Delivery slot options"
          className="absolute left-0 right-0 mt-1.5 overflow-hidden rounded-xl border border-gray-100 bg-white shadow-xl"
          style={{ animation: "slotDropdownIn 0.16s ease", transformOrigin: "top left", zIndex: 50 }}
        >
          {/* Panel header */}
          <div className="border-b border-gray-100 px-3 py-2">
            <p className="text-[10px] font-bold uppercase tracking-widest text-gray-400">
              Choose Slot
            </p>
          </div>

          {/* Placeholder option */}
          <ul className="p-1.5 max-h-60 overflow-y-auto">
            {!value && (
              <li>
                <div className="flex items-center gap-2 rounded-lg px-2 py-2 text-xs text-gray-400 italic">
                  <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-gray-100">
                    <FaClock className="h-3 w-3 text-gray-300" />
                  </span>
                  {placeholder}
                </div>
              </li>
            )}

            {/* Grouped slot rows */}
            {Object.entries(effectiveGroups).map(([day, daySlots]) => {
              const cfg = getDayConfig(day);
              return (
                <li key={day}>
                  {/* Day label */}
                  <div
                    className="mx-2 mb-1 mt-2 flex items-center gap-1.5 first:mt-1"
                  >
                    <span
                      className="rounded-md px-2 py-0.5 text-[10px] font-bold"
                      style={{ background: cfg.bg, color: cfg.color }}
                    >
                      {day}
                    </span>
                    <span className="flex-1 border-t border-gray-100" />
                  </div>

                  {/* Slots */}
                  <ul>
                    {daySlots.map((slot) => {
                      const slotVal = slot.value || slot.label;
                      const slotLbl = slot.label || slot.value;
                      const isActive = String(value) === String(slotVal) || String(value) === String(slot.slot_id);
                      return (
                        <li key={slot.slot_id || slotVal}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={isActive}
                            onClick={() => handleSelect(slot)}
                            className="flex w-full items-center gap-2 rounded-lg px-2 py-2 text-left transition-all duration-150 hover:bg-gray-50 focus:outline-none"
                            style={isActive ? { background: cfg.bg } : {}}
                          >
                            {/* Icon */}
                            <span
                              className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg transition-all duration-150"
                              style={{
                                background: isActive ? cfg.color : cfg.bg,
                                color: isActive ? "#fff" : cfg.color,
                              }}
                            >
                              <FaClock className="h-3 w-3" aria-hidden="true" />
                            </span>

                            {/* Text */}
                            <span className="flex-1">
                              <span
                                className="block text-xs font-semibold"
                                style={{ color: isActive ? cfg.color : "#1f2937" }}
                              >
                                {slotLbl}
                              </span>
                            </span>

                            {/* Active tick */}
                            {isActive && (
                              <span
                                className="flex h-4 w-4 flex-shrink-0 items-center justify-center rounded-full text-white text-[9px] font-black"
                                style={{ background: cfg.color }}
                                aria-hidden="true"
                              >
                                ✓
                              </span>
                            )}
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      <style>{`
        @keyframes slotDropdownIn {
          from { opacity: 0; transform: scale(0.94) translateY(-5px); }
          to   { opacity: 1; transform: scale(1)    translateY(0);    }
        }
      `}</style>
    </div>
  );
};

export default SlotDropdown;
